/**
 * Shared question type definitions, smart input hints, and validation.
 *
 * Used by all 3 survey wizards (Brand, Campaign, Lemonade) and
 * admin components to ensure consistent behavior.
 */

export type QuestionType =
  | 'single_choice'
  | 'multiple_choice'
  | 'open_ended'
  | 'rating'
  | 'scale'
  | 'date';

export type InputHintKind = 'name' | 'zip' | 'email' | 'phone' | 'default';

export interface QuestionInputHint {
  kind: InputHintKind;
  inputMode: 'text' | 'numeric' | 'email' | 'tel';
  placeholder: string;
  maxLength?: number;
  autoComplete?: string;
  isLongAnswer: boolean;
  validate: (value: string) => string | null;
}

const NAME_RE = /\bname\b/i;
const FIRST_NAME_RE = /\bfirst\s*name\b/i;
const LAST_NAME_RE = /\blast\s*name\b/i;
const ZIP_RE = /\b(zip|postal)\b/i;
const EMAIL_RE = /\bemail\b/i;
const PHONE_RE = /\b(phone|mobile|cell)\b/i;
const LONG_ANSWER_RE = /\b(describe|explain|tell us|why|how do you|thoughts|opinion|comment)\b/i;

const EMAIL_VALIDATE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_US_RE = /^\d{5}(-\d{4})?$/;
const ZIP_CA_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
const DIGITS_RE = /\d/;

function validateName(value: string): string | null {
  const v = value.trim();
  if (!v) return 'This field is required';
  if (v.length > 50) return 'Name must be 50 characters or less';
  if (DIGITS_RE.test(v)) return 'Name should not contain numbers';
  return null;
}

function validateZip(value: string): string | null {
  const v = value.trim();
  if (!v) return 'This field is required';
  if (ZIP_US_RE.test(v) || ZIP_CA_RE.test(v)) return null;
  return 'Please enter a valid zip/postal code';
}

function validateEmail(value: string): string | null {
  const v = value.trim();
  if (!v) return 'This field is required';
  if (!EMAIL_VALIDATE_RE.test(v)) return 'Please enter a valid email address';
  return null;
}

function validatePhone(value: string): string | null {
  const v = value.replace(/\D/g, '');
  if (!v) return 'This field is required';
  if (v.length < 10) return 'Please enter a valid phone number';
  return null;
}

function validateRequired(value: string): string | null {
  return value.trim() ? null : 'This field is required';
}

/**
 * Detect the intent of an open_ended question from its text and return
 * appropriate input hints, mobile attributes, and a validation function.
 */
export function getInputHint(questionText: string): QuestionInputHint {
  const q = questionText.toLowerCase();

  if (FIRST_NAME_RE.test(q)) {
    return {
      kind: 'name', inputMode: 'text', placeholder: 'Enter your first name',
      maxLength: 50, autoComplete: 'given-name', isLongAnswer: false, validate: validateName,
    };
  }
  if (LAST_NAME_RE.test(q)) {
    return {
      kind: 'name', inputMode: 'text', placeholder: 'Enter your last name',
      maxLength: 50, autoComplete: 'family-name', isLongAnswer: false, validate: validateName,
    };
  }
  if (NAME_RE.test(q) && !LAST_NAME_RE.test(q) && !FIRST_NAME_RE.test(q)) {
    return {
      kind: 'name', inputMode: 'text', placeholder: 'Enter your name',
      maxLength: 50, autoComplete: 'name', isLongAnswer: false, validate: validateName,
    };
  }
  if (ZIP_RE.test(q)) {
    return {
      kind: 'zip', inputMode: 'text', placeholder: 'Enter zip/postal code',
      maxLength: 10, autoComplete: 'postal-code', isLongAnswer: false, validate: validateZip,
    };
  }
  if (EMAIL_RE.test(q)) {
    return {
      kind: 'email', inputMode: 'email', placeholder: 'Enter your email',
      maxLength: 254, autoComplete: 'email', isLongAnswer: false, validate: validateEmail,
    };
  }
  if (PHONE_RE.test(q)) {
    return {
      kind: 'phone', inputMode: 'tel', placeholder: 'Enter phone number',
      maxLength: 20, autoComplete: 'tel', isLongAnswer: false, validate: validatePhone,
    };
  }

  const isLong = LONG_ANSWER_RE.test(q);
  return {
    kind: 'default', inputMode: 'text', placeholder: 'Type your answer...',
    isLongAnswer: isLong, validate: validateRequired,
  };
}
